import { DOCUMENT } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Renderer2,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Environment, ENVIRONMENT_NAME } from '@freelancer/config';
import { Seo } from '@freelancer/seo';
import { ThemeService } from '@freelancer/theme';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { ContainerSize } from '@freelancer/ui/container';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { LinkHoverColor } from '@freelancer/ui/link';
import { PictureLayout, PictureObjectFit } from '@freelancer/ui/picture';
import { isDefined } from '@freelancer/utils';
import type { Observable } from 'rxjs';
import { filter, map, Subscription } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-home',
  template: `
    <app-nav
      [hideInternalPages]="hideInternalPages"
      [isHomepage]="true"
      (menuButtonClicked)="toggleMobileSidebar()"
    />

    <div class="MainContainer">
      <div
        *ngIf="(storyOnly$ | flAsync) === false"
        [flHideDesktop]="true"
        class="MainSidebar"
      >
        <div
          class="MainSidebar-backdrop"
          [attr.data-sidebar-open]="sidebarOpen"
          [attr.tabindex]="0"
          [flHideDesktop]="true"
          (click)="toggleMobileSidebar()"
        ></div>
        <app-sidebar
          class="MainSidebar-content"
          [attr.data-sidebar-open]="sidebarOpen"
          [hideInternalPages]="hideInternalPages"
          (closeSidebar)="toggleMobileSidebar()"
        />
      </div>

      <div class="HomeContainer">
        <fl-container [size]="ContainerSize.DESKTOP_XLARGE">
          <div class="mb-large desktop:mb-xxxlarge">
            <div class="Hero mb-xlarge">
              <div class="mb-large">
                <fl-picture
                  alt="Bits Logo illustration"
                  [src]="
                    (currentTheme$ | flAsync) === 'dark'
                      ? 'bits/bits-logo-dark.svg'
                      : 'bits/bits-logo-light.svg'
                  "
                  [fullWidth]="false"
                  [layout]="PictureLayout.FILL"
                  [objectFit]="PictureObjectFit.COVER"
                />
              </div>

              <h2
                class="Headline text-foreground text-small tablet:text-mid font-normal mb-xlarge"
              >
                Easily build functional, beautiful interfaces that match the
                brand, principles, and language of Freelancer’s design system.
              </h2>

              <fl-button
                class="Hero-button mb-small tablet:mb-none"
                [color]="ButtonColor.PRIMARY"
                [size]="ButtonSize.LARGE"
                [link]="'/info/introduction'"
              >
                Get started
              </fl-button>
            </div>

            <fl-grid
              class="Grid"
              [vAlign]="VerticalAlignment.VERTICAL_STRETCH"
            >
              <fl-col
                class="HomepageFeatures mb-large"
                [col]="12"
                [colDesktopSmall]="hideInternalPages ? 6 : 4"
              >
                <fl-link
                  [hoverColor]="
                    (currentTheme$ | flAsync) === 'dark'
                      ? LinkHoverColor.DEFAULT
                      : LinkHoverColor.INHERIT
                  "
                  [link]="'/foundations'"
                >
                  <fl-picture
                    alt="Foundations illustration"
                    [src]="
                      (currentTheme$ | flAsync) === 'dark'
                        ? 'bits/foundations-dark.png'
                        : 'bits/foundations-light.png'
                    "
                    [fullWidth]="true"
                  />
                  <h3 class="text-mid HomepageFeatures-heading">Foundations</h3>
                  <p>
                    Foundations are the core visual elements needed to create
                    engaging layouts and end-to-end user experiences.
                  </p>
                </fl-link>
              </fl-col>

              <fl-col
                class="HomepageFeatures"
                [col]="12"
                [colDesktopSmall]="hideInternalPages ? 6 : 4"
              >
                <fl-link
                  [hoverColor]="
                    (currentTheme$ | flAsync) === 'dark'
                      ? LinkHoverColor.DEFAULT
                      : LinkHoverColor.INHERIT
                  "
                  [link]="'/components'"
                >
                  <fl-picture
                    alt="Components illustration"
                    [src]="
                      (currentTheme$ | flAsync) === 'dark'
                        ? 'bits/components-dark.png'
                        : 'bits/components-light.png'
                    "
                    [fullWidth]="true"
                  />
                  <h3 class="text-mid HomepageFeatures-heading">Components</h3>
                  <p>
                    The building blocks that make up our interfaces such as
                    buttons, form fields, and other interactive elements.
                  </p>
                </fl-link>
              </fl-col>

              @if (!hideInternalPages) {
                <fl-col
                  class="HomepageFeatures"
                  [col]="12"
                  [colDesktopSmall]="4"
                >
                  <fl-link
                    [hoverColor]="
                      (currentTheme$ | flAsync) === 'dark'
                        ? LinkHoverColor.DEFAULT
                        : LinkHoverColor.INHERIT
                    "
                    [link]="'/patterns'"
                  >
                    <fl-picture
                      alt="Patterns illustration"
                      [src]="
                        (currentTheme$ | flAsync) === 'dark'
                          ? 'bits/patterns-dark.png'
                          : 'bits/patterns-light.png'
                      "
                      [fullWidth]="true"
                    />
                    <h3 class="text-mid HomepageFeatures-heading">Patterns</h3>
                    <p>
                      Shared Freelancer-specific components for core UI sections
                      to maintain consistency between different products.
                    </p>
                  </fl-link>
                </fl-col>
              }
            </fl-grid>
          </div>
        </fl-container>

        <div class="Footer">
          <p class="text-foreground text-xxsmall tablet:text-xsmall">
            {{ '© Freelancer.com 2025' }}
          </p>

          <p class="text-foreground text-xxsmall tablet:text-xsmall">
            {{ 'Made with' }}
            <fl-icon
              [name]="'ui-heart'"
              [useIconFont]="true"
              [fillIconFont]="true"
              [color]="IconColor.ERROR"
              [size]="IconSize.SMALL"
            />
            {{ 'by UI Engineering and Design team' }}
          </p>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./home.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent implements OnInit, OnDestroy {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  ContainerSize = ContainerSize;
  IconColor = IconColor;
  LinkHoverColor = LinkHoverColor;
  VerticalAlignment = VerticalAlignment;
  PictureObjectFit = PictureObjectFit;
  PictureLayout = PictureLayout;
  IconSize = IconSize;

  currentTheme$: Observable<string>;

  sidebarOpen = false;
  hideInternalPages = false;
  storyOnly$: Observable<boolean>;

  private subscriptions = new Subscription();

  constructor(
    private seo: Seo,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private themeService: ThemeService,
    private renderer: Renderer2,
    @Inject(ENVIRONMENT_NAME) private environment: Environment,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngOnInit(): void {
    this.currentTheme$ = this.themeService.getTheme();
    this.renderer.removeStyle(this.document.body, 'overflow');

    this.seo.setPageTags({
      title: 'Home',
      description: `A live showcase of Freelancer.com's official design system, Bits`,
    });

    this.storyOnly$ = this.activatedRoute.queryParamMap.pipe(
      map(params => isDefined(params.get('story_only'))),
    );

    this.subscriptions.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => {
          this.sidebarOpen = false;
        }),
    );

    this.hideInternalPages = this.environment === 'prod';
  }

  nav(path: string): void {
    this.router.navigate([path]);
  }

  toggleMobileSidebar(): void {
    if (this.sidebarOpen) {
      this.renderer.removeStyle(this.document.body, 'overflow');
    } else {
      this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
    }
    this.sidebarOpen = !this.sidebarOpen;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
